import { useEffect, useState } from 'react'
import { App, Card, Col, Row, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

interface Row {
  machine_id: string
  machine_code: string
  machine_name: string
  department_id: string | null
  pm_template_id: string | null
  frequency: string
  planned_count: number
  completed_count: number
  pending_count: number
  overdue_count: number
  deferred_count: number
  pm_compliance_pct: number
  last_pm_at: string | null
  next_pm_at: string | null
  assigned_technician: string | null
}

/**
 * Spec §6 — PM compliance report. Compliance below 80% renders in red.
 */
export default function PMComplianceReportPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const { deptName } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId,
    from_date: dayjs().subtract(90, 'day').format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
  })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-pm-compliance', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.pmCompliance(filters) as unknown as Promise<Row[]>,
  })
  const rows = data ?? []

  const totalPlanned = rows.reduce((a, r) => a + (r.planned_count ?? 0), 0)
  const totalCompleted = rows.reduce((a, r) => a + (r.completed_count ?? 0), 0)
  const totalOverdue = rows.reduce((a, r) => a + (r.overdue_count ?? 0), 0)
  const totalDeferred = rows.reduce((a, r) => a + (r.deferred_count ?? 0), 0)
  const overall = totalPlanned ? (totalCompleted / totalPlanned * 100) : 0

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/pm-compliance', filters,
        `pm-compliance-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) { message.error((e as Error).message ?? 'Export failed') }
    finally { setExporting(false) }
  }

  const columns = [
    { title: 'Machine', dataIndex: 'machine_code', key: 'mc', width: 130 },
    { title: 'Name', dataIndex: 'machine_name', key: 'name', ellipsis: true },
    { title: 'Department', key: 'd', width: 160,
      render: (_: unknown, r: Row) => deptName(r.department_id),
    },
    { title: 'Frequency', dataIndex: 'frequency', key: 'f', width: 110 },
    { title: 'Planned', dataIndex: 'planned_count', key: 'p', width: 90 },
    { title: 'Completed', dataIndex: 'completed_count', key: 'c', width: 100 },
    { title: 'Pending', dataIndex: 'pending_count', key: 'pn', width: 90 },
    { title: 'Overdue', dataIndex: 'overdue_count', key: 'od', width: 90,
      render: (v: number) => v > 0 ? <Tag color="red">{v}</Tag> : v,
    },
    { title: 'Deferred', dataIndex: 'deferred_count', key: 'df', width: 90 },
    { title: 'Compliance %', dataIndex: 'pm_compliance_pct', key: 'cp', width: 130,
      render: (v: number) => (
        <Text strong style={{ color: v < 80 ? '#cf1322' : '#52c41a' }}>
          {v.toFixed(1)}%
        </Text>
      ),
    },
    { title: 'Last PM', dataIndex: 'last_pm_at', key: 'lp', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM') : '—',
    },
    { title: 'Next PM', dataIndex: 'next_pm_at', key: 'np', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM') : '—',
    },
  ]

  return (
    <div>
      <PageHeader title="PM Compliance"
        subtitle="Tracks whether preventive maintenance discipline is being followed." />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ dateRange: true, department: true, machine: true, frequency: true }}
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={4}><Card><Statistic title="Planned" value={totalPlanned} /></Card></Col>
        <Col xs={12} md={4}><Card><Statistic title="Completed" value={totalCompleted} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={12} md={4}><Card><Statistic title="Overdue" value={totalOverdue} valueStyle={{ color: '#cf1322' }} /></Card></Col>
        <Col xs={12} md={4}><Card><Statistic title="Deferred" value={totalDeferred} /></Card></Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title="Overall compliance"
              value={overall}
              suffix="%"
              precision={1}
              valueStyle={{ color: overall < 80 ? '#cf1322' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>
      <DataTable rowKey={(r: Row) => `${r.machine_id}-${r.pm_template_id ?? ''}-${r.frequency}`}
        columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
